import { useMutation, useQueryClient, useQuery, keepPreviousData, type QueryClient } from "@tanstack/react-query";
import { useToast } from "../../../components/toast/useToast";
import KontrolyServiceApi from "./KontrolyService";
import { QueryError } from "../../QueryError";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import type { TypKontroly, TypKontrolyRequest, NahledTypKontrolyRequest, NahledTypKontrolyResponse } from "../generated/models/TypKontroly";
import type {
    UzivatelKontrola,
    CreateKontrolaRequest,
    KontrolaStatusResponse,
    KontrolyOverviewResponse,
} from "../generated/models/Kontrola";

/** Invalidace dotazu zavislych na typech kontrol (po zmene typu, jeho stavu nebo smazani). */
const invalidujKontroly = (queryClient: QueryClient) => {
    queryClient.invalidateQueries({ queryKey: ["typyKontrol"] });
    queryClient.invalidateQueries({ queryKey: ["kontrolyOverview"] });
    queryClient.invalidateQueries({ queryKey: ["kontrolyUser"] });
    queryClient.invalidateQueries({ queryKey: ["typyKontrolSplnitelneUcasti"] });
};

const jeValidacniChyba = (error: Error): boolean =>
    error instanceof QueryError && error.statusCode === 400;

// ==========================================
// QUERIES
// ==========================================

export const useTypyKontrolQuery = () => {
    return useQuery<TypKontroly[], Error>({
        queryKey: ["typyKontrol"],
        queryFn: () => KontrolyServiceApi.getAllTypyKontrol(),
        staleTime: 5 * 60 * 1000,
    });
};

export const useTypyKontrolSplnitelneUcastiQuery = () => {
    return useQuery<TypKontroly[], Error>({
        queryKey: ["typyKontrolSplnitelneUcasti"],
        queryFn: () => KontrolyServiceApi.getTypyKontrolSplnitelneUcasti(),
        staleTime: 5 * 60 * 1000,
    });
};

export const useKontrolyForUserQuery = (uzivatelId: number | null) => {
    return useQuery<KontrolaStatusResponse[], Error>({
        queryKey: ["kontrolyUser", uzivatelId],
        queryFn: () => KontrolyServiceApi.getKontrolyForUser(uzivatelId!),
        enabled: !!uzivatelId,
    });
};

export const useKontrolyOverviewQuery = () => {
    return useQuery<KontrolyOverviewResponse, Error>({
        queryKey: ["kontrolyOverview"],
        queryFn: () => KontrolyServiceApi.getKontrolyOverview(),
    });
};

export const useKontrolyHistorieQuery = (uzivatelId: number | null, typId: number | null) => {
    return useQuery<UzivatelKontrola[], Error>({
        queryKey: ["kontrolyHistorie", uzivatelId, typId],
        queryFn: () => KontrolyServiceApi.getKontrolyHistorie(uzivatelId!, typId!),
        enabled: !!uzivatelId && !!typId,
    });
};

// ==========================================
// KONTROLA MUTATIONS
// ==========================================

export const useCreateKontrolaMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<UzivatelKontrola, Error, CreateKontrolaRequest>({
        mutationFn: (data) => KontrolyServiceApi.createKontrola(data),

        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["kontrolyUser", variables.uzivatelId] });
            queryClient.invalidateQueries({ queryKey: ["kontrolyOverview"] });
            queryClient.invalidateQueries({ queryKey: ["kontrolyHistorie"] });
            showToast("Kontrola byla zaznamenána.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se zaznamenat kontrolu.", "error");
        },
    });
};

export const useUpdateKontrolaMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<UzivatelKontrola, Error, { id: number; data: CreateKontrolaRequest }>({
        mutationFn: ({ id, data }) => KontrolyServiceApi.updateKontrola(id, data),

        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["kontrolyUser", variables.data.uzivatelId] });
            queryClient.invalidateQueries({ queryKey: ["kontrolyOverview"] });
            queryClient.invalidateQueries({ queryKey: ["kontrolyHistorie"] });
            showToast("Kontrola byla aktualizována.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se aktualizovat kontrolu.", "error");
        },
    });
};

export const useDeleteKontrolaMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, { id: number; uzivatelId: number }>({
        mutationFn: ({ id }) => KontrolyServiceApi.deleteKontrola(id),

        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["kontrolyUser", variables.uzivatelId] });
            queryClient.invalidateQueries({ queryKey: ["kontrolyOverview"] });
            queryClient.invalidateQueries({ queryKey: ["kontrolyHistorie"] });
            showToast("Záznam kontroly byl smazán.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se smazat záznam kontroly.", "error");
        },
    });
};

// ==========================================
// TYP KONTROLY MUTATIONS
// ==========================================

export const useCreateTypKontrolyMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<TypKontroly, Error, TypKontrolyRequest>({
        mutationFn: (data) => KontrolyServiceApi.createTypKontroly(data),

        onSuccess: () => {
            invalidujKontroly(queryClient);
            showToast("Typ kontroly byl vytvořen.", "success");
        },

        onError: (error) => {
            // 400 (validace nahrazovani apod.) zobrazi dialog formulare jako Alert, toast by ji zdvojil
            if (jeValidacniChyba(error)) {
                return;
            }
            showToast(error.message || "Nepodařilo se vytvořit typ kontroly.", "error");
        },
    });
};

export const useUpdateTypKontrolyMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<TypKontroly, Error, { id: number; data: TypKontrolyRequest }>({
        mutationFn: ({ id, data }) => KontrolyServiceApi.updateTypKontroly(id, data),

        onSuccess: () => {
            invalidujKontroly(queryClient);
            showToast("Typ kontroly byl aktualizován.", "success");
        },

        onError: (error) => {
            // 400 (validace nahrazovani apod.) zobrazi dialog formulare jako Alert, toast by ji zdvojil
            if (jeValidacniChyba(error)) {
                return;
            }
            showToast(error.message || "Nepodařilo se aktualizovat typ kontroly.", "error");
        },
    });
};

export const useDeleteTypKontrolyMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: (id) => KontrolyServiceApi.deleteTypKontroly(id),

        onSuccess: () => {
            invalidujKontroly(queryClient);
            showToast("Typ kontroly byl smazán.", "success");
        },

        onError: (error) => {
            // 409 MA_HISTORII resi dialog smazani (hlaska + nabidka deaktivace), toast by ji zdvojil
            if (error instanceof QueryError && error.statusCode === 409) {
                return;
            }
            showToast(error.message || "Nepodařilo se smazat typ kontroly.", "error");
        },
    });
};

export const useToggleTypDashboardMutation = () => {
    const queryClient = useQueryClient();

    return useMutation<{ success: boolean }, Error, { id: number; zobrazitDashboard: boolean }>({
        mutationFn: ({ id, zobrazitDashboard }) => KontrolyServiceApi.toggleTypDashboard(id, zobrazitDashboard),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["typyKontrol"] });
            queryClient.invalidateQueries({ queryKey: ["kontrolyOverview"] });
            queryClient.invalidateQueries({ queryKey: ["technikaKontrolyOverview"] });
        },
    });
};

export const useNastavTypAktivniMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<{ success: boolean }, Error, { id: number; aktivni: boolean }>({
        mutationFn: ({ id, aktivni }) => KontrolyServiceApi.nastavTypAktivni(id, aktivni),

        onSuccess: (_data, variables) => {
            invalidujKontroly(queryClient);
            showToast(variables.aktivni ? "Kontrola byla aktivována." : "Kontrola byla deaktivována.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se změnit stav kontroly.", "error");
        },
    });
};

/** Zivy nahled dopadu typu kontroly; request se posila s prodlevou 400 ms (spec 6.1 bod 3). */
export const useNahledTypKontrolyQuery = (request: NahledTypKontrolyRequest | null) => {
    const debounced = useDebouncedValue(request, 400);
    return useQuery<NahledTypKontrolyResponse, Error>({
        queryKey: ["nahledTypKontroly", debounced],
        queryFn: () => KontrolyServiceApi.nahledTypKontroly(debounced as NahledTypKontrolyRequest),
        enabled: debounced !== null,
        placeholderData: keepPreviousData,
        staleTime: 0,
    });
};
